import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertCircle, CalendarDays, MessageSquareText, RefreshCw } from "lucide-react";
import { format, isToday, isYesterday, startOfDay, subDays } from "date-fns";
import { el } from "date-fns/locale";
import PartnerPageHeader from "@/components/partners/PartnerPageHeader";
import PartnerTimeFilter, { TimeRangeType } from "@/components/partners/PartnerTimeFilter";
import PartnerMetricCard from "@/components/partners/PartnerMetricCard";
import LeadDetailsModal from "@/components/partners/LeadDetailsModal";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getPartnerHeaders } from "@/integrations/php/client";
import { usePartnerLeads } from "@/hooks/usePartnerLeads";
import { PartnerActivityLead, PartnerActivityNote, parsePortalDate } from "@/types/partnerPortal";
import { cn } from "@/lib/utils";

type ActivityFilter = "all" | "leads" | "notes";

const rangeStart = (range: TimeRangeType) => {
  if (range === "today") return startOfDay(new Date());
  if (range === "7d") return subDays(new Date(), 7);
  if (range === "30d") return subDays(new Date(), 30);
  if (range === "90d") return subDays(new Date(), 90);
  return new Date(0);
};

const dateHeading = (date: Date) => {
  if (isToday(date)) return "Σήμερα";
  if (isYesterday(date)) return "Χθες";
  return format(date, "EEEE, d MMMM", { locale: el });
};

const PartnerActivityPage = () => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const { markViewed } = usePartnerLeads(partnerId);
  const [leads, setLeads] = useState<PartnerActivityLead[]>([]);
  const [notes, setNotes] = useState<PartnerActivityNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRangeType>("30d");
  const [filter, setFilter] = useState<ActivityFilter>("all");
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  const loadActivity = useCallback(async (initial = false) => {
    if (!partnerId) return;
    if (initial) setIsLoading(true);
    else setIsFetching(true);
    setError(null);
    try {
      const response = await fetch(`/api/get-partner-activity.php?partner_id=${encodeURIComponent(partnerId)}`, {
        credentials: "include",
        headers: getPartnerHeaders(),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error("Δεν φορτώθηκε η δραστηριότητα.");
      setLeads((body.leads || []).map((lead: Record<string, string>) => ({ id: lead.id, name: lead.name, stage: lead.stage, createdAt: lead.created_at, updatedAt: lead.updated_at })));
      setNotes((body.notes || []).map((note: Record<string, string>) => ({ id: note.id, leadId: note.lead_id, note: note.note, authorName: note.author_name, createdAt: note.created_at })));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Δεν φορτώθηκε η δραστηριότητα.");
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  }, [partnerId]);

  useEffect(() => { void loadActivity(true); }, [loadActivity]);

  const events = useMemo(() => {
    const start = rangeStart(timeRange);
    const leadNames = new Map(leads.map((lead) => [lead.id, lead.name]));
    const items = [
      ...leads.filter((lead) => parsePortalDate(lead.createdAt) >= start).map((lead) => ({
        id: `lead-${lead.id}`,
        type: "leads" as const,
        leadId: lead.id,
        leadName: lead.name,
        title: "Νέα ανάθεση",
        description: "Το lead προστέθηκε στις εργασίες σας.",
        date: parsePortalDate(lead.createdAt),
      })),
      ...notes.filter((note) => parsePortalDate(note.createdAt) >= start).map((note) => ({
        id: `note-${note.id}`,
        type: "notes" as const,
        leadId: note.leadId,
        leadName: leadNames.get(note.leadId) || "Lead",
        title: "Νέα σημείωση",
        description: note.note,
        date: parsePortalDate(note.createdAt),
      })),
    ].filter((event) => filter === "all" || event.type === filter);
    return items.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [leads, notes, timeRange, filter]);

  const groups = useMemo(() => {
    const result = new Map<string, typeof events>();
    events.forEach((event) => {
      const key = format(event.date, "yyyy-MM-dd");
      result.set(key, [...(result.get(key) || []), event]);
    });
    return Array.from(result.entries());
  }, [events]);

  const periodLeadCount = events.filter((event) => event.type === "leads").length;
  const periodNoteCount = events.filter((event) => event.type === "notes").length;

  return (
    <div className="space-y-6">
      <LeadDetailsModal leadId={selectedLeadId} onClose={() => setSelectedLeadId(null)} partnerId={partnerId} onViewed={markViewed} />
      <PartnerPageHeader
        eyebrow="Χρονολόγιο"
        title="Δραστηριότητα"
        description="Οι νέες αναθέσεις και οι σημειώσεις σας, σε μία καθαρή χρονολογική σειρά."
        actions={<Button variant="outline" onClick={() => void loadActivity()} disabled={isFetching} className="h-11 rounded-xl bg-white"><RefreshCw className={cn("mr-2 h-4 w-4", isFetching && "animate-spin")} /> Ανανέωση</Button>}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <PartnerMetricCard label="Ενέργειες" value={events.length} hint="Στην επιλεγμένη περίοδο" icon={<CalendarDays className="h-5 w-5" />} tone="slate" />
        <PartnerMetricCard label="Νέες αναθέσεις" value={periodLeadCount} hint="Leads που προστέθηκαν" icon={<AlertCircle className="h-5 w-5" />} tone="blue" />
        <div className="col-span-2 lg:col-span-1"><PartnerMetricCard label="Σημειώσεις" value={periodNoteCount} hint="Καταγεγραμμένες ενέργειες" icon={<MessageSquareText className="h-5 w-5" />} tone="emerald" /></div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex rounded-xl border border-slate-200 bg-white p-1">
          {([{ value: "all", label: "Όλα" }, { value: "leads", label: "Αναθέσεις" }, { value: "notes", label: "Σημειώσεις" }] as Array<{ value: ActivityFilter; label: string }>).map((item) => (
            <button key={item.value} type="button" onClick={() => setFilter(item.value)} className={cn("h-10 rounded-lg px-3 text-xs font-semibold", filter === item.value ? "bg-slate-900 text-white" : "text-slate-600")}>{item.label}</button>
          ))}
        </div>
        <PartnerTimeFilter value={timeRange} onChange={setTimeRange} />
      </div>

      {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-medium text-rose-800">{error}</div>}
      {isLoading ? (
        <div className="space-y-3"><Skeleton className="h-28 rounded-2xl" /><Skeleton className="h-28 rounded-2xl" /><Skeleton className="h-28 rounded-2xl" /></div>
      ) : groups.length ? (
        <div className="space-y-6">
          {groups.map(([dateKey, dayEvents]) => (
            <section key={dateKey}>
              <h2 className="mb-3 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">{dateHeading(dayEvents[0].date)}</h2>
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                {dayEvents.map((event, index) => {
                  const NoteIcon = event.type === "notes" ? MessageSquareText : AlertCircle;
                  return (
                    <button
                      key={event.id}
                      type="button"
                      onClick={() => setSelectedLeadId(event.leadId)}
                      className={cn("flex w-full items-start gap-3 px-4 py-4 text-left transition-colors hover:bg-slate-50 sm:px-5", index > 0 && "border-t border-slate-100")}
                    >
                      <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", event.type === "notes" ? "bg-emerald-50 text-emerald-700" : "bg-sky-50 text-sky-700")}><NoteIcon className="h-5 w-5" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between">
                          <span className="text-sm font-bold text-slate-900">{event.title} · {event.leadName}</span>
                          <span className="text-xs text-slate-500">{format(event.date, "HH:mm")}</span>
                        </span>
                        <span className="mt-1 line-clamp-2 block text-sm leading-6 text-slate-600">{event.description}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-14 text-center"><CalendarDays className="mx-auto h-8 w-8 text-slate-300" /><h2 className="mt-3 font-bold text-slate-900">Καμία δραστηριότητα</h2><p className="mt-1 text-sm text-slate-500">Αλλάξτε φίλτρο ή χρονική περίοδο.</p></div>
      )}
    </div>
  );
};

export default PartnerActivityPage;
